import { colors } from '@loggi/mar';
import { Box, Container, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { ReactComponent as MyIcon } from './SVG.svg';

const useBannerStyles = makeStyles(() => ({
  bannerContainer: {
    background: '#003399',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignSelf: 'stretch',
    borderRadius: '20px',
    boxShadow: '0 16px 40px 0 rgba(0, 0, 0, 0.10)',
    marginBottom: '40px'
  },
  bannerLeft: {
    display: 'flex',
    width: '224px',
    padding: '28px 17px 28px 16px',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'stretch'
  },
  bannerCenter: {
    display: 'flex',
    padding: '24px',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: '16px',
    flex: '1 0 0',
    alignSelf: 'stretch'
  },
  bannerCenterBadge: {
    display: 'flex',
    padding: '4px 8px',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '9999px',
    background: '#05F'
  },
  bannerCenterBadgeTypo: {
    color: '#FFF',
    fontFamily: 'Montserrat',
    fontSize: '8px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '12px',
    letterSpacing: '0.48px',
    textTransform: 'uppercase'
  },
  bannerCenterContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
    alignSelf: 'stretch'
  },
  bannerCenterContentTitleTypo: {
    color: '#FFF',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontFamily: 'Montserrat',
    fontSize: '20px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '32px',
    alignSelf: 'stretch'
  },
  bannerCenterContentDescriptionTypo: {
    color: '#FFF',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontFamily: 'Montserrat',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '20px',
    alignSelf: 'stretch'
  },
  bannerRight: {
    display: 'flex',
    width: '264px',
    padding: '24px',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    gap: '16px',
    alignSelf: 'stretch'
  },
  bannerRightButton: {
    display: 'flex',
    padding: '10px',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '6px',
    background: '#05F'
  },
  bannerRightButtonTypo: {
    color: '#FFF',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontFamily: 'Montserrat',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '20px'
  }
}));
export const NewExperienceBanner = () => {
  const styles = useBannerStyles();

  return (
    <Box className={styles.bannerContainer}>
      <Box className={styles.bannerLeft}>
        <MyIcon />
      </Box>
      <Box className={styles.bannerCenter}>
        <Box className={styles.bannerCenterBadge}>
          <Typography className={styles.bannerCenterBadgeTypo}>novo</Typography>
        </Box>
        <Box className={styles.bannerCenterContent}>
          <Typography className={styles.bannerCenterContentTitleTypo}>
            Encerramento do portal
          </Typography>
          <Typography className={styles.bannerCenterContentDescriptionTypo}>
            A partir de 12/01/2026 este portal será encerrado. Experimente agora
            a nova versão e veja como ficou mais fácil enviar com a Loggi.
          </Typography>
        </Box>
      </Box>
      <Box className={styles.bannerRight}>
        <a
          href="https://app.loggi.com/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none' }}
        >
          <Box className={styles.bannerRightButton}>
            <Typography className={styles.bannerRightButtonTypo}>
              Conhecer agora
            </Typography>
          </Box>
        </a>
      </Box>
    </Box>
  );
};

const useSummaryStyles = makeStyles(({ typography, breakpoints, palette }) => ({
  root: {
    backgroundImage: ({ color }) =>
      color ? 'none' : palette.header?.gradient || colors.gradients.sanches,
    backgroundColor: ({ color }) => color || 'transparent',
    transition: 'background-color 0.5s ease-in-out',
    [breakpoints.up('sm')]: {
      minHeight: typography.pxToRem(292)
    }
  }
}));

/**
 * This is an extra padding used to free some space on the top of the page
 * @type {Float|Object} - Material spacing unit
 */
const HEADER_EXTRA_PADDING = { xs: 8, sm: 10 };

/**
 * The OneTemplateSummary is the component responsible of presenting the elements
 * of the upper region of the template
 * @param children
 * @param color
 * @param showBanner
 * @returns {*}
 * @constructor
 */
const OneTemplateSummary = ({ children, color, showBanner }) => {
  const { palette } = useTheme();
  const style = useSummaryStyles({ color });

  return (
    <Box
      alignItems="flex-end"
      className={style.root}
      display="flex"
      flexShrink={0}
      pb={8}
      pt={HEADER_EXTRA_PADDING}
    >
      <Container
        color={palette.common.white}
        component={Box}
        pb={{ xs: 3, sm: 5 }}
        pt={{ xs: 5, sm: 7 }}
      >
        {showBanner && <NewExperienceBanner />}
        {children}
      </Container>
    </Box>
  );
};

OneTemplateSummary.propTypes = {
  color: PropTypes.string,
  showBanner: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};

OneTemplateSummary.defaultProps = {
  color: '',
  showBanner: false
};

export default OneTemplateSummary;
